"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";

const RE_JSON_STRING =
  /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g;
const RE_STARTS_WITH_QUOTE = /^"/;
const RE_ENDS_WITH_COLON = /:"?$/;
const RE_BOOL = /true|false/;
const RE_NULL = /null/;
const RE_AMP = /&/g;
const RE_LT = /</g;
const RE_GT = />/g;

function getTokenClass(match: string): string {
  if (RE_STARTS_WITH_QUOTE.test(match)) {
    if (RE_ENDS_WITH_COLON.test(match)) {
      return "text-purple-600 dark:text-purple-400 font-medium";
    }
    return "text-green-700 dark:text-green-400";
  }
  if (RE_BOOL.test(match)) {
    return "text-orange-600 dark:text-orange-400";
  }
  if (RE_NULL.test(match)) {
    return "text-red-600 dark:text-red-400";
  }
  return "text-blue-600 dark:text-blue-400";
}

function syntaxHighlight(json: string): string {
  const escaped = json
    .replace(RE_AMP, "&amp;")
    .replace(RE_LT, "&lt;")
    .replace(RE_GT, "&gt;");

  return escaped.replace(RE_JSON_STRING, (match) => {
    const cls = getTokenClass(match);
    return `<span class="${cls}">${match}</span>`;
  });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

type Status = "idle" | "valid" | "invalid";

/** English strings. A translated content file overrides these through `ui`. */
export const JSON_FORMATTER_STRINGS = {
  indentSpaces: "Indent spaces:",
  jsonInput: "JSON input",
  /* Two counts around one separator — see markdown-to-text for the same shape. */
  linesAndBytes: "{lines} · {bytes}",
  lineCount: "one: {count} line|other: {count} lines",
  byteCount: "one: {count} byte|other: {count} bytes",
  valid: "Valid",
  validJson: "Valid JSON",
  error: "Error",
  invalidJson: "Invalid JSON",
  format: "Format",
  minify: "Minify",
  validateOnly: "Validate only",
  copied: "Copied!",
  copyOutput: "Copy Output",
  clear: "Clear",
  inputSize: "Input size",
  outputSize: "Output size",
  sizeChange: "Size change",
  formattedOutput: "Formatted output",
} as const;

export default function JsonFormatterWidget() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [copied, setCopied] = useState(false);
  const [indent, setIndent] = useState(2);

  function parseInput() {
    try {
      return { parsed: JSON.parse(input), err: null };
    } catch (e) {
      return { parsed: null, err: e instanceof Error ? e.message : String(e) };
    }
  }

  function handleFormat() {
    if (input.trim().length === 0) {
      return;
    }
    const { parsed, err } = parseInput();
    if (err !== null) {
      setError(err);
      setStatus("invalid");
      setOutput("");
      return;
    }
    setError("");
    setStatus("valid");
    setOutput(JSON.stringify(parsed, null, indent));
    setCopied(false);
  }

  function handleMinify() {
    if (input.trim().length === 0) {
      return;
    }
    const { parsed, err } = parseInput();
    if (err !== null) {
      setError(err);
      setStatus("invalid");
      setOutput("");
      return;
    }
    setError("");
    setStatus("valid");
    setOutput(JSON.stringify(parsed));
    setCopied(false);
  }

  function handleValidate() {
    if (input.trim().length === 0) {
      return;
    }
    const { err } = parseInput();
    if (err !== null) {
      setError(err);
      setStatus("invalid");
      setOutput("");
    } else {
      setError("");
      setStatus("valid");
      setOutput("");
    }
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setError("");
    setStatus("idle");
    setCopied(false);
  }

  const inputSize = new Blob([input]).size;
  const outputSize = new Blob([output]).size;
  const inputLines = input.trim().length === 0 ? 0 : input.split("\n").length;

  const strings = useToolUi(JSON_FORMATTER_STRINGS);
  const locale = useToolLocale();

  return (
    <>
      {/* Options */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-foreground text-sm">
          {strings.indentSpaces}
          <select
            className="rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => setIndent(Number(e.target.value))}
            value={indent}
          >
            <option value={2}>2</option>
            <option value={4}>4</option>
            <option value={8}>8</option>
          </select>
        </label>
      </div>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="json-input"
        >
          {strings.jsonInput}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="json-input"
          onChange={(e) => {
            setInput(e.target.value);
            setStatus("idle");
            setError("");
            setOutput("");
          }}
          placeholder={'{"key": "value", "number": 42}'}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {t(strings.linesAndBytes, {
            lines: tPlural(strings.lineCount, locale, inputLines).trim(),
            bytes: tPlural(strings.byteCount, locale, inputSize).trim(),
          })}
        </p>
      </div>
      {/* Validation status */}
      {status === "valid" && (
        <p className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-2 text-green-700 text-sm dark:text-green-300">
          <span aria-label={strings.valid} role="img">
            ✓
          </span>{" "}
          {strings.validJson}
        </p>
      )}
      {status === "invalid" && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
          <p className="flex items-center gap-2 font-medium text-red-700 text-sm dark:text-red-300">
            <span aria-label={strings.error} role="img">
              ✗
            </span>{" "}
            {strings.invalidJson}
          </p>
          <p className="mt-1 font-mono text-red-700 text-xs dark:text-red-300">
            {error}
          </p>
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleFormat}
          type="button"
        >
          {strings.format}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleMinify}
          type="button"
        >
          {strings.minify}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleValidate}
          type="button"
        >
          {strings.validateOnly}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyOutput}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.inputSize} value={`${inputSize} B`} />
          <StatCard label={strings.outputSize} value={`${outputSize} B`} />
          <StatCard
            label={strings.sizeChange}
            value={
              inputSize > 0
                ? `${outputSize > inputSize ? "+" : ""}${Math.round(((outputSize - inputSize) / inputSize) * 100)}%`
                : "—"
            }
          />
        </div>
      )}
      {/* Output with syntax highlighting */}
      {output.length > 0 && (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.formattedOutput}
          </p>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <pre
              className="overflow-x-auto p-4 font-mono text-sm leading-relaxed"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: controlled JSON syntax highlight
              dangerouslySetInnerHTML={{ __html: syntaxHighlight(output) }}
            />
          </div>
        </div>
      )}
    </>
  );
}
